import React, { useState, useCallback } from 'react';
import { FiArrowLeft, FiArrowRight, FiAlertCircle, FiInfo } from 'react-icons/fi';

interface CsvRow {
  [key: string]: string;
}

interface RowError {
  row: number;
  reason: string;
  data: any;
}

interface ColumnMappingStepProps {
  rows: CsvRow[];
  mapping: Record<string, string>;
  onComplete: (mapping: Record<string, string>, errors: RowError[]) => void;
  onBack: () => void;
  onClose: () => void;
}

const REQUIRED_FIELDS = [
  // Required fields
  { key: 'display_name', label: 'Display Name', required: true, category: 'required' },
  { key: 'billing_addr_line1', label: 'Street Address', required: true, category: 'required' },
  { key: 'billing_addr_city', label: 'City', required: true, category: 'required' },
  { key: 'billing_addr_state', label: 'State', required: true, category: 'required' },
  { key: 'billing_addr_zip', label: 'ZIP Code', required: true, category: 'required' },
  
  // Personal information
  { key: 'first_name', label: 'First Name', required: false, category: 'personal' },
  { key: 'last_name', label: 'Last Name', required: false, category: 'personal' },
  { key: 'title', label: 'Title', required: false, category: 'personal' },
  { key: 'suffix', label: 'Suffix', required: false, category: 'personal' },
  { key: 'company_name', label: 'Company Name', required: false, category: 'personal' },
  { key: 'website', label: 'Website', required: false, category: 'personal' },
  { key: 'notes', label: 'Notes', required: false, category: 'personal' },
  
  // Contact information
  { key: 'email', label: 'Email', required: false, category: 'contact' },
  { key: 'phone', label: 'Phone', required: false, category: 'contact' },
  { key: 'mobile', label: 'Mobile', required: false, category: 'contact' },
  { key: 'fax', label: 'Fax', required: false, category: 'contact' },
  { key: 'cc_email', label: 'CC Email', required: false, category: 'contact' },
  { key: 'bcc_email', label: 'BCC Email', required: false, category: 'contact' },
  { key: 'name_on_checks', label: 'Name on Checks', required: false, category: 'contact' },
  
  // Billing address
  { key: 'billing_addr_line2', label: 'Address Line 2', required: false, category: 'billing' },
  { key: 'billing_addr_country', label: 'Country', required: false, category: 'billing' },
  
  // Shipping address
  { key: 'shipping_addr_line1', label: 'Shipping Street', required: false, category: 'shipping' },
  { key: 'shipping_addr_line2', label: 'Shipping Address Line 2', required: false, category: 'shipping' },
  { key: 'shipping_addr_city', label: 'Shipping City', required: false, category: 'shipping' },
  { key: 'shipping_addr_state', label: 'Shipping State', required: false, category: 'shipping' },
  { key: 'shipping_addr_zip', label: 'Shipping ZIP', required: false, category: 'shipping' },
  { key: 'shipping_addr_country', label: 'Shipping Country', required: false, category: 'shipping' },
  { key: 'shipping_same_as_billing', label: 'Same as Billing', required: false, category: 'shipping' },
  
  // Tax information
  { key: 'tax_exempt', label: 'Tax Exempt', required: false, category: 'tax' },
  { key: 'tax_exempt_doc_url', label: 'Tax Exempt Doc URL', required: false, category: 'tax' },
  
  // Pricing information
  { key: 'custom_prices', label: 'Custom Prices', required: false, category: 'pricing' },

  // Financials
  { key: 'starting_balance', label: 'Starting Balance', required: false, category: 'financials' },
];

export const ColumnMappingStep: React.FC<ColumnMappingStepProps> = ({
  rows,
  mapping,
  onComplete,
  onBack,
  onClose
}) => {
  const [currentMapping, setCurrentMapping] = useState<Record<string, string>>(mapping);
  const [errors, setErrors] = useState<RowError[]>([]);

  const csvHeaders = rows.length > 0 ? Object.keys(rows[0]) : [];

  const validateMapping = useCallback(() => {
    const newErrors: RowError[] = [];
    
    // Check for missing required fields
    const missingRequired = REQUIRED_FIELDS
      .filter(field => field.required && !currentMapping[field.key])
      .map(field => field.label);

    if (missingRequired.length > 0) {
      // Add validation errors for first few rows
      const sampleRows = rows.slice(0, 3);
      sampleRows.forEach((row, index) => {
        newErrors.push({
          row: index + 1,
          reason: `Missing required field: ${missingRequired.join(', ')}`,
          data: row
        });
      });
    }

    // Validate individual rows
    rows.forEach((row, index) => {
      const rowErrors = validateRow(row, currentMapping);
      if (rowErrors.length > 0) {
        newErrors.push({
          row: index + 1,
          reason: rowErrors.join(', '),
          data: row
        });
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  }, [rows, currentMapping]);

  const validateRow = (row: CsvRow, mapping: Record<string, string>): string[] => {
    const errors: string[] = [];
    
    // Check required fields
    REQUIRED_FIELDS.filter(field => field.required).forEach(field => {
      const csvHeader = mapping[field.key];
      if (!csvHeader || !row[csvHeader]?.trim()) {
        errors.push(`Missing ${field.label}`);
      }
    });

    // Check contact information (email OR phone OR mobile required)
    const emailHeader = mapping.email;
    const phoneHeader = mapping.phone;
    const mobileHeader = mapping.mobile;
    const hasEmail = emailHeader && row[emailHeader]?.trim();
    const hasPhone = phoneHeader && row[phoneHeader]?.trim();
    const hasMobile = mobileHeader && row[mobileHeader]?.trim();
    
    if (!hasEmail && !hasPhone && !hasMobile) {
      errors.push('Email, Phone, or Mobile required');
    }

    return errors;
  };

  const handleMappingChange = useCallback((fieldKey: string, csvHeader: string) => {
    setCurrentMapping(prev => ({
      ...prev,
      [fieldKey]: csvHeader
    }));
  }, []);

  const handleNext = useCallback(() => {
    if (validateMapping()) {
      onComplete(currentMapping, errors);
    }
  }, [validateMapping, currentMapping, errors, onComplete]);

  const canProceed = REQUIRED_FIELDS
    .filter(field => field.required)
    .every(field => currentMapping[field.key]);

  return (
    <div className="p-6 pb-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-2">
            Map CSV Columns
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400">
            Map your CSV columns to the correct customer fields. Required fields are marked with an asterisk (*).
          </p>
        </div>

        {/* Column Mapping Table */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
              <thead className="bg-neutral-50 dark:bg-neutral-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">
                    Field
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">
                    CSV Column
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">
                    Sample Value
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                {REQUIRED_FIELDS.map((field) => {
                  const selectedHeader = currentMapping[field.key];
                  const sampleValue = selectedHeader && rows[0] ? rows[0][selectedHeader] : '';
                  
                  return (
                    <tr key={field.key} className={field.category === 'required' ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900 dark:text-neutral-50">
                        <div className="flex items-center">
                          <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                            field.category === 'required' ? 'bg-red-500' :
                            field.category === 'personal' ? 'bg-blue-500' :
                            field.category === 'contact' ? 'bg-green-500' :
                            field.category === 'billing' ? 'bg-yellow-500' :
                            field.category === 'shipping' ? 'bg-purple-500' :
                            field.category === 'pricing' ? 'bg-orange-500' :
                            field.category === 'financials' ? 'bg-teal-500' :
                            'bg-gray-500'
                          }`}></span>
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-50">
                        <select
                          value={selectedHeader || ''}
                          onChange={(e) => handleMappingChange(field.key, e.target.value)}
                          className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand dark:bg-neutral-700 dark:text-neutral-50"
                        >
                          <option value="">Select column...</option>
                          {csvHeaders.map(header => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600 dark:text-neutral-400">
                        {sampleValue ? (
                          <span className="truncate max-w-xs block" title={sampleValue}>
                            {sampleValue}
                          </span>
                        ) : (
                          <span className="text-neutral-400">No value</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Custom Pricing Help Text */}
        {currentMapping.custom_prices && (
          <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md">
            <div className="flex items-start">
              <FiInfo className="w-4 h-4 text-orange-500 mr-2 mt-0.5" />
              <div className="text-sm text-orange-700 dark:text-orange-300">
                <p className="font-medium mb-1">Custom Pricing Format:</p>
                <p className="mb-1">Use "Item Name : Price" pairs separated by semicolons.</p>
                <p className="text-xs">Examples: "Grass Cut:40.00; Mulch Install:120.00" or "Lawn Care:1,200.00; Hedge Trim:.50"</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Summary */}
        {errors.length > 0 && (
          <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <div className="flex items-start">
              <FiAlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                  Validation Errors Found
                </h4>
                <div className="text-sm text-red-700 dark:text-red-300 space-y-1">
                  {errors.slice(0, 5).map((error, index) => (
                    <div key={index}>
                      <strong>Row {error.row}:</strong> {error.reason}
                    </div>
                  ))}
                  {errors.length > 5 && (
                    <div className="text-red-600 dark:text-red-400">
                      ... and {errors.length - 5} more errors
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
          >
            <FiArrowLeft className="mr-2" size={16} />
            Back
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              onClick={handleNext}
              disabled={!canProceed}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <FiArrowRight className="ml-2" size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 